import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import type { NextPage } from 'next';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';

import { Container, InputText, Select } from '@dansk-metal/ui';
import { formatDate } from '@dansk-metal/utils/date';

import { BlockType } from '@web/blocks';

import { EmptyState } from '@web/components/empty-state/empty-state';
import { HeroTitle } from '@web/components/hero-block/hero-title/hero-title';
import { Pagination } from '@web/components/pagination/pagination';
import { PublicationItem } from '@web/components/publication-item/publication-item';

import TemplateLayout from '@web/layouts/template/template';

import { PublicationsSearchResult } from '@web/services/umbraco/rest';
import { HeroAlignment } from '@web/services/umbraco/types/basic/Alignment';
import { BackgroundColor } from '@web/services/umbraco/types/basic/Color';
import { HTMLString } from '@web/services/umbraco/types/basic/RichTextContent';
import { UmbracoPage } from '@web/services/umbraco/types/documents/UmbracoDocument';

import { RelativeUrl } from '@web/utils/routes';
import { sortDanish } from '@web/utils/sort-danish';

import { useBeforeShallowRouteStart } from '@web/hooks/use-before-shallow-route-start';
import { useShallowQueryParams } from '@web/hooks/use-query-param';

import { usePublicationSearchForm } from './hooks/use-publication-search-form';

import styles from './publication-overview-page.module.scss';

const contentAlias = 'publicationsOverviewPage' as const;

export type PublicationsOverviewPageData = UmbracoPage<
	typeof contentAlias,
	{
		heroTitle: HTMLString;
		heroSubtitle?: string;
		blocks: BlockType[];
	},
	{
		tags: string[];
		publications: PublicationsSearchResult;
	}
>;

const defaultTag = { value: '', label: 'Alle emner' };

const PublicationsOverviewPageTemplate: NextPage<{ pageData: PublicationsOverviewPageData }> = ({ pageData }) => {
	const router = useRouter();
	const pathname = usePathname();
	const { publications: defaultPublications, tags } = pageData;
	const { heroTitle, heroSubtitle } = pageData.properties;
	const pageFromUrl = parseInt(router.query.page as string, 10) || 1;

	const {
		control, publications, pagination, setValue, setTagValue, submit, handleSubmit, getValues, watch, formState,
	} = usePublicationSearchForm({
		limit: 24,
		publications: defaultPublications,
		pathname: pathname || '',
	});

	const select = Select.useSelect<{ value: string; label: string; shouldDirty?: boolean }, false>({
		isMulti: false,
		onChange: (option) => setTagValue({ value: option.value, shouldDirty: option.shouldDirty }),
	});

	const selectOptions = tags.map((tag) => ({ value: tag, label: tag })).sort((a, b) => sortDanish(a.label, b.label));

	const { updateUrlParam, urlStateLoaded } = useShallowQueryParams({
		routerQueryParamChanged: () => {
			const tagFromUrl = selectOptions.find((tag) => tag.value === router.query.tag);

			if (tagFromUrl) {
				select.onChange({ ...tagFromUrl, shouldDirty: false });
			} else if (select.value !== defaultTag) {
				select.onChange({ ...defaultTag, shouldDirty: false });
			}

			setValue('text', (router.query.text as string) || '', { shouldDirty: false });
			pagination.setPage(pageFromUrl);
		},
	});

	useEffect(() => {
		if (!urlStateLoaded) {
			return;
		}

		// If the form is dirty, reset the pagination. Dirty is not triggered if the form value is set programmatically
		if (formState.isDirty) {
			pagination.firstPage();
		}

		updateUrlParam({ ...getValues() });
		handleSubmit(submit)();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to run this effect when the form state changes
	}, [watch('text'), watch('tag'), formState.isDirty]);

	useEffect(() => {
		if (!urlStateLoaded || !window) return;
		handleSubmit(submit)();
		updateUrlParam({ page: pagination.page > 1 ? pagination.page.toString() : '' });
		window.scrollTo(0, 0);
	// eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to run this effect when the pagination page changes
	}, [pagination.page]);

	// Effect handler for when the user navigates within the page using the browser back/forward buttons
	useBeforeShallowRouteStart((targetUrl) => {
		const params = new RelativeUrl(targetUrl).searchParams;
		select.onChange({ ...(selectOptions.find((tag) => tag.value === params.get('tag')) || defaultTag), shouldDirty: true });
		setValue('text', params.get('text') || '', { shouldDirty: true });
		pagination.setPage(parseInt(params.get('page') || '1', 10));
	});

	return (
		<TemplateLayout>
			{!!heroTitle && (
				<HeroTitle
					title={heroTitle}
					subtitle={heroSubtitle || undefined}
					alignment={HeroAlignment.Left}
					background={BackgroundColor.White}
				/>
			)}

			<Container>
				<form className={styles.filter}>
					<Controller
						control={control}
						name="text"
						render={({ field }) => (
							<InputText {...field} noOptionalLabel label="Søg pjecer og udgivelser" icon={{ icon: 'search' }} />
						)}
					/>
					<Select
						{...select}
						noOptionalLabel
						label="Emne"
						options={[defaultTag, ...selectOptions]}
						placeholder={defaultTag.label}
						isSearchable
					/>
				</form>
			</Container>
			<div className={styles.publications_container}>
				{urlStateLoaded && publications.length !== 0 ? (
					<Container className={styles.container}>
						<div className={styles.publication_list}>
							{publications.map((publicationItem) => (
								<PublicationItem
									key={publicationItem.id}
									publication={{
										...publicationItem,
										publishDate: publicationItem.publishDate
											? formatDate(publicationItem.publishDate, '22. feb 2022')
											: null,
									}}
								/>
							))}
						</div>
						{(pagination.maxPages > 1) && (
							<Pagination
								{...pagination}
								pageLink={(page) => {
									const params = new URLSearchParams(window.location.search);
									params.set('page', page.toString());
									return `${pathname}?${params.toString()}`;
								}}
							/>
						)}
					</Container>
				) : (
					<EmptyState
						title='Der er ingen publikationer eller udgivelser at vise'
						bodyText='Der er ingen publikationer eller udgivelser, som matcher de filtre du har sat.'
					/>
				)}
			</div>
		</TemplateLayout>
	);
};

export default PublicationsOverviewPageTemplate;
